import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { Box, FormLabel, FormErrorMessage, Input, VStack, HStack, Text, Spinner, IconButton } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { ChevronDown, ChevronRight, X } from 'lucide-react'

interface TreeOption {
  id: number | string
  name: string
  label?: string
  children?: TreeOption[]
}

interface TreeDropdownProps {
  options?: TreeOption[]
  values?: string | null
  onChange?: (value: string) => void
  loadOptions?: (parentId: number | null, callback: (data: TreeOption[]) => void) => void
  placeholder?: string
  isRequired?: boolean
  isInvalid?: boolean
  errorMessage?: string
  label?: string
  isDisabled?: boolean
}

export interface TreeDropdownRef {
  getValue: () => { id: number | null }
}

export const TreeDropdown = forwardRef<TreeDropdownRef, TreeDropdownProps>(
  (
    {
      options = [],
      values,
      onChange,
      loadOptions,
      placeholder = 'Select option',
      label,
      isInvalid = false,
      errorMessage,
      isDisabled = false,
    },
    ref
  ) => {
    const [parentNodes, setParentNodes] = useState<TreeOption[]>(options)
    const [childNodes, setChildNodes] = useState<Record<number | string, TreeOption[]>>({})
    const [expandedOptions, setExpandedOptions] = useState<Set<number | string>>(new Set())
    const [selectedOption, setSelectedOption] = useState<TreeOption | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loadingState, setLoadingState] = useState<Record<number | string, boolean>>({})
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const targetIdRef = useRef<number | string | null>(null)

    const colors = useThemeColors()
    const bgColor = colors.cardBg
    const borderColor = colors.border
    const hoverBg = colors.sectionBg

    useEffect(() => {
      if (options.length > 0) {
        setParentNodes(options)
      } else if (loadOptions && parentNodes.length === 0) {
        setIsLoading(true)
        loadOptions(null, (data) => {
          setParentNodes(data)
          setIsLoading(false)
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options])

    const findOptionInTree = (opts: TreeOption[], targetId: number | string): TreeOption | null => {
      for (const opt of opts) {
        if (String(opt.id) === String(targetId)) return opt
        if (opt.children) {
          const found = findOptionInTree(opt.children, targetId)
          if (found) return found
        }
      }
      return null
    }

    const findAndSetSelectedOption = (targetId: number | string, autoLoad = false) => {
      let found = findOptionInTree(parentNodes, targetId)

      if (!found) {
        for (const children of Object.values(childNodes)) {
          found = findOptionInTree(children, targetId)
          if (found) break
        }
      }

      if (found) {
        setSelectedOption(found)
        return true
      }

      if (autoLoad && loadOptions && parentNodes.length > 0) {
        const loadAndFind = (nodes: TreeOption[], depth = 0): void => {
          if (depth > 5) return

          for (const node of nodes) {
            if (loadingState[node.id] || childNodes[node.id] || node.children) {
              const childrenToCheck = childNodes[node.id] || node.children || []
              const foundInChildren = findOptionInTree(childrenToCheck, targetId)
              if (foundInChildren) {
                setSelectedOption(foundInChildren)
                return
              }
              if (childrenToCheck.length > 0) {
                loadAndFind(childrenToCheck, depth + 1)
              }
              continue
            }

            setLoadingState((prev) => ({ ...prev, [node.id]: true }))
            setExpandedOptions((prev) => new Set(prev).add(node.id))

            loadOptions(Number(node.id), (data) => {
              setChildNodes((prev) => ({ ...prev, [node.id]: data }))
              setLoadingState((prev) => ({ ...prev, [node.id]: false }))

              const foundInChildren = findOptionInTree(data, targetId)
              if (foundInChildren) {
                setSelectedOption(foundInChildren)
                return
              }

              if (data.length > 0) {
                loadAndFind(data, depth + 1)
              }
            })
          }
        }

        loadAndFind(parentNodes)
      }

      return false
    }

    useEffect(() => {
      if (values) {
        try {
          const parsed = JSON.parse(values)
          if (parsed.id) {
            targetIdRef.current = parsed.id
            if (!findAndSetSelectedOption(parsed.id, true)) {
              setSelectedOption(null)
            }
          } else {
            targetIdRef.current = null
            setSelectedOption(null)
          }
        } catch {
          targetIdRef.current = null
          setSelectedOption(null)
        }
      } else {
        targetIdRef.current = null
        setSelectedOption(null)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values])

    useEffect(() => {
      if (targetIdRef.current && (!selectedOption || String(selectedOption.id) !== String(targetIdRef.current))) {
        findAndSetSelectedOption(targetIdRef.current, true)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentNodes, childNodes])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false)
          setSearchQuery('')
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        const timer = setTimeout(() => searchInputRef.current?.focus(), 100)
        return () => clearTimeout(timer)
      }
    }, [isOpen])

    const handleToggleExpand = (option: TreeOption) => {
      const isExpanded = expandedOptions.has(option.id)

      if (isExpanded) {
        setExpandedOptions((prev) => {
          const next = new Set(prev)
          next.delete(option.id)
          return next
        })
      } else {
        setExpandedOptions((prev) => new Set(prev).add(option.id))

        if (!childNodes[option.id] && !option.children && loadOptions) {
          setLoadingState((prev) => ({ ...prev, [option.id]: true }))
          loadOptions(Number(option.id), (data) => {
            setChildNodes((prev) => ({ ...prev, [option.id]: data }))
            setLoadingState((prev) => ({ ...prev, [option.id]: false }))
          })
        }
      }
    }

    const handleSelectOption = (option: TreeOption) => {
      setSelectedOption(option)
      setIsOpen(false)
      setSearchQuery('')
      const value = JSON.stringify({ id: option.id })
      onChange?.(value)
    }

    const handleClear = () => {
      setSelectedOption(null)
      onChange?.('')
    }

    const filterOptions = (opts: TreeOption[], query: string): TreeOption[] => {
      if (!query) return opts
      const lowerQuery = query.toLowerCase()
      return opts.filter((opt) => {
        const nameMatch =
          opt.name.toLowerCase().includes(lowerQuery) ||
          (opt.label && opt.label.toLowerCase().includes(lowerQuery))
        const childrenMatch = opt.children ? filterOptions(opt.children, query).length > 0 : false
        return nameMatch || childrenMatch
      })
    }

    const renderOption = (option: TreeOption, level = 0) => {
      const hasChildren = (childNodes[option.id]?.length ?? 0) > 0 || (option.children?.length ?? 0) > 0
      const isExpanded = expandedOptions.has(option.id)
      const isLoadingChild = loadingState[option.id] === true
      const isSelected = selectedOption?.id === option.id
      const indent = level * 20

      return (
        <Box key={option.id}>
          <HStack
            spacing={2}
            px={2}
            py={1.5}
            pl={`${indent + 8}px`}
            cursor="pointer"
            bg={isSelected ? 'blue.50' : 'transparent'}
            _hover={{ bg: hoverBg }}
            onClick={() => handleSelectOption(option)}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Box
              w="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={(e) => {
                e.stopPropagation()
                if (hasChildren || loadOptions) {
                  handleToggleExpand(option)
                }
              }}
              cursor={hasChildren || loadOptions ? 'pointer' : 'default'}
            >
              {isLoadingChild ? (
                <Spinner size="xs" />
              ) : hasChildren || loadOptions ? (
                isExpanded ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )
              ) : (
                <Box w="16px" />
              )}
            </Box>
            <Text flex={1} fontSize="sm">
              {option.label || option.name}
            </Text>
          </HStack>
          {isExpanded && hasChildren && (
            <Box>{(childNodes[option.id] || option.children || []).map((child) => renderOption(child, level + 1))}</Box>
          )}
        </Box>
      )
    }

    const displayOptions = searchQuery ? filterOptions(parentNodes, searchQuery) : parentNodes

    useImperativeHandle(ref, () => ({
      getValue: () => {
        if (!selectedOption) return { id: null }
        return { id: Number(selectedOption.id) }
      },
    }))

    return (
      <Box ref={dropdownRef} position="relative">
        {label && <FormLabel mb={2}>{label}</FormLabel>}
        <Box
          position="relative"
          borderWidth="1px"
          borderColor={isInvalid ? 'red.500' : borderColor}
          borderRadius="md"
          bg={bgColor}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          opacity={isDisabled ? 0.6 : 1}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
        >
          <HStack px={3} py={2} justify="space-between" minH="40px">
            <Text flex={1} fontSize="sm" color={selectedOption ? 'inherit' : 'gray.500'} noOfLines={1}>
              {selectedOption ? selectedOption.label || selectedOption.name : placeholder}
            </Text>
            <HStack spacing={1}>
              {selectedOption && !isDisabled && (
                <IconButton
                  aria-label="Clear selection"
                  icon={<X size={14} />}
                  size="xs"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClear()
                  }}
                />
              )}
              <Box>
                <ChevronDown size={16} />
              </Box>
            </HStack>
          </HStack>
        </Box>

        {isOpen && !isDisabled && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            boxShadow="lg"
            zIndex={1000}
            maxH="300px"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <Box p={2} borderBottomWidth="1px" borderColor={borderColor}>
              <Input
                ref={searchInputRef}
                size="sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>

            <Box overflowY="auto" flex={1}>
              {isLoading && parentNodes.length === 0 ? (
                <VStack py={4}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">
                    Loading...
                  </Text>
                </VStack>
              ) : displayOptions.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Text fontSize="sm" color="gray.500">
                    No options found
                  </Text>
                </Box>
              ) : (
                <VStack spacing={0} align="stretch" py={1}>
                  {displayOptions.map((option) => renderOption(option))}
                </VStack>
              )}
            </Box>
          </Box>
        )}

        {errorMessage && <FormErrorMessage mt={1}>{errorMessage}</FormErrorMessage>}
      </Box>
    )
  }
)

TreeDropdown.displayName = 'TreeDropdown'


